import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '@environments/environment';
import { SigninBody, SigninResponse, CurrentUser, AuthData } from '@app/models';
import { Observable, throwError } from 'rxjs';
import { ForgotPasswordBody, forgotPasswordResponse } from '@app/models/forgot-password.model';
import { SetPasswordRequest, SetPasswordResponse } from '@app/models/set-password';
import { ValidateTokenRequest, ValidateTokenResponse } from '@app/models/token-validation';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { CommonService, IdleService, LocalStorageService, NotificationService } from '@app/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AnalyticAPIReqLabels, AnalyticLogLabels, ERROR_STRINGS, GLOBAL_ERROR_MESSAGES, SNACKBAR_NOTIFICATION, SNACK_TIMEOUT, StatusCode } from '@app/shared';
import { OrganizationList } from '@app/models/organization.model';
import { AnalyticService } from '@app/core/analytic.service';
import { IntercomService } from '@app/core/intercom.service';


@Injectable({
  providedIn: 'root'
})
export class LoginService {

  baseUrl = environment.apiBaseUrl;

  constructor(
    private readonly http: HttpClient,
    private readonly _router: Router,
    private readonly _snackBar: MatSnackBar,
    private readonly _analytics: AnalyticService,
    private readonly _commonService: CommonService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _notificationService: NotificationService, 
    private readonly _intercomService: IntercomService,
    private readonly _idleService: IdleService
  ) { }

  signIn(body: SigninBody): Observable<SigninResponse> {
    return this.http.post<SigninResponse>(
      `${this.baseUrl}/auth/signIn`,
      body,
      { headers: { 'method-name': AnalyticAPIReqLabels.signIn } }
    )
      .pipe(
        map(response => {
          if (response.accessToken) {
            let authData = new AuthData(response.accessToken);
            this._localStorageService.setAuthData(authData);
          }
          return response;
        })
      );
  }

  /**
   * Verifies the OTP for a user
   * @param emailOtp The OTP entered by the user
   * @returns Observable<{ message: string; accessToken?: string }>
   */
  verifyOtp(emailOtp: number): Observable<{ message: string; accessToken?: string }> {
    const requestBody = { email_otp: emailOtp };
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this._localStorageService.getAuthData()?.accessToken || ''}`,
    };

    return this.http
      .post<{ message: string; accessToken?: string }>(
        `${this.baseUrl}/auth/verifyotp`,
        requestBody,
        { headers }
      )
      .pipe(
        tap(
          (response) => {
            // Handle successful OTP verification
            if (response.accessToken) {
              this._localStorageService.setAuthData(new AuthData(response.accessToken));
            }
          },
          (error) => {
            // Handle client-side or network errors
            if (error instanceof HttpErrorResponse) {
              if (error.status === StatusCode.Unauthorized) {
                this.handleUnauthorized(error);
              } else if (error.status === StatusCode.Badrequest) {
                this._notificationService.showError(error, 'Incorrect OTP, Please try again');
              }
            }
          }
        ),
        catchError((error) => {
          if (error.name === ERROR_STRINGS.TIMEOUT) {
            this._commonService.setScreenStatus({ status: 'Error', message: GLOBAL_ERROR_MESSAGES.REQUEST_TIMEOUT });
          }
          return throwError(error);
        }),
        finalize(() => {
          this._commonService.setNavLoading(false);
        })
      );
  }

  /**
   * Resend the OTP to the user
   * @returns Observable<{ message: string }>
   */
  resendOtp(): Observable<{ message: string }> {
    const headers = {
      Authorization: `Bearer ${this._localStorageService.getAuthData()?.accessToken || ''}`,
    };

    return this.http.post<{ message: string }>(
      `${this.baseUrl}/auth/resendOtp`,
      null,
      { headers }
    ).pipe(
      tap(
        (response) => {
          this._snackBar.open(response.message || 'OTP sent successfully', '', { duration: SNACK_TIMEOUT });
        },
        (error) => {
          if (error.status === 401) {
            this.handleUnauthorized(error);
          } else {
            this._notificationService.showError(error, 'Failed to resend OTP. Please try again.');
          }
        }
      ),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  /**
   * Handles unauthorized responses and navigates the user to the sign-in page
   */
  private handleUnauthorized(error: HttpErrorResponse): void {
    this._localStorageService.clearStorage();
    this._idleService.stopWatching();
    this._router.navigate(['auth/signin']).then(() => {
      this._notificationService.showError(error || 'Unauthorized access');
    });
  }

  /**
   * Clear the local data
   * navigate to no auth module
   * log the signout analytics
   * set app and account data to null
   * trigger the signout notification.
   */
  logout() {
    localStorage.clear();
    this._idleService.stopWatching();
    this._router.navigate(['auth/signin'])
      .then(() => {
        this._analytics.logAnalyticEvent(AnalyticLogLabels.UserLogout);
        this._analytics.setAnalytics(null);
        this._intercomService.logout();
        this._commonService.setAppProfileData({
          accessToken: '',
          user: {} as CurrentUser,
          organizations: [] as OrganizationList[],
          currentOrganization: {} as OrganizationList,
          currentSeniorCenter: null
        });
        this._commonService.setOrgSelectorList(null);
        this._snackBar.open(SNACKBAR_NOTIFICATION.SIGNOUT, '', { duration: SNACK_TIMEOUT });
      });
  }

  /**
   * Request's the forget password API with email
   * @param requestBody 
   * @returns forgotPasswordResponse
   */
  requestForgetPassword(requestBody: ForgotPasswordBody): Observable<forgotPasswordResponse> {
    return this.http.post<forgotPasswordResponse>(
      `${this.baseUrl}/auth/forgotPassword`,
      requestBody,
      { headers: { 'method-name': AnalyticAPIReqLabels.forgotPassword } }
    );
  }

  requestSetPassword(requestBody: SetPasswordRequest): Observable<SetPasswordResponse> {
    return this.http.post<SetPasswordResponse>(
      `${this.baseUrl}/auth/setPassword`,
      requestBody,
      { headers: { 'method-name': AnalyticAPIReqLabels.setPassword } }
    );
  }

  /**
   * Request's the validate API with email and accesstoken
   * @param requestBody 
   * @returns ValidateTokenResponse
   */
  validateToken(requestBody: ValidateTokenRequest): Observable<ValidateTokenResponse> {
    return this.http.post<ValidateTokenResponse>(
      `${this.baseUrl}/auth/validateToken`,
      requestBody,
      { headers: { 'method-name': AnalyticAPIReqLabels.validateToken } }
    );
  }

}
